/*
 * CUSTOM CODE START
 *
 * FAAN cart drawer extras — rotating messages, threshold rewards engine,
 * free-gift picker sheet, upsell carousel, variant picker sheet and the
 * unlock confetti.
 *
 * Written as a CLASSIC script (this theme has no ES modules and no import map):
 * one IIFE, custom elements extending HTMLElement, wiring in init(), and the
 * theme's own `data-js-*` attribute convention instead of Horizon-style refs.
 *
 * How state flows
 * ---------------
 * Every cart mutation in this theme re-fetches `?section_id=helper-cart` and
 * swaps the innerHTML of #AjaxCartForm and #AjaxCartSubtotal
 * (assets/component-cart.js). snippets/faan-cart-reward-cards.liquid therefore
 * re-renders a fresh JSON snapshot of the reward state on every change, and
 * this file simply reads it back after each swap — a MutationObserver on
 * #AjaxCartForm is the trigger. All decisions are made from that snapshot;
 * nothing about reward state is cached client-side except the session-scoped
 * "dismissed" / "failed" / "celebrated" memories below.
 *
 * Cart mutations go out as plain fetches and are followed by
 * window.refreshCart(false), the theme's canonical re-render entry point.
 *
 * Line item property contract (shared with the Liquid snippets):
 *   _omaggio  → a reward freebie
 *   _regalo   → the gift block product
 *   _reward   → the reward slot number (1-4) a freebie was redeemed for
 */

(function () {
  'use strict';

  if (window.__faanCartExtras) return;
  window.__faanCartExtras = true;

  /* ----------------------------------------------------------------------
     Constants and engine state
     ---------------------------------------------------------------------- */

  var DISMISS_KEY = 'faanCartRewardDismissed';
  var FAILED_KEY = 'faanCartRewardFailed';
  var CELEBRATED_KEY = 'faanCartRewardCelebrated';

  // Bursts of cart events can make every pass react with a request; without
  // spacing those can hit Shopify's rate limit. One scheduled mutation at a
  // time, with a minimum gap between requests.
  var MUTATION_COOLDOWN = 1500;
  var PASS_DEBOUNCE = 100;

  // Safety net for a reward-slot claim whose add never resolves (offline, a
  // request that hangs). Long enough not to reopen the double-add window on a
  // slow connection, short enough that the shopper is not stuck.
  var SLOT_CLAIM_TIMEOUT = 8000;

  var lastMutationAt = 0;
  var mutationTimer = null;
  var passTimer = null;
  var clearingCart = false;
  var redeemingReward = false;
  var selfRefreshing = false;
  var celebrationBaselineDone = false;
  var drawerWasOpen = false;

  var lastRedeemed = {}; // slot -> was redeemed on the previous pass
  var inFlight = {}; // slot -> mutation in progress
  var snoozedSlots = {}; // slot -> dismissed with the X until the drawer reopens
  var upsellCache = { productId: null, html: '' };
  var confettiContext = null;
  var confettiPromise = null;

  function routes() {
    return (window.KROWN && window.KROWN.settings && window.KROWN.settings.routes) || {};
  }

  function engineBusy() {
    if (clearingCart || redeemingReward || mutationTimer !== null) return true;
    for (var slot in inFlight) {
      if (inFlight[slot]) return true;
    }
    return false;
  }

  function runMutationSpaced(fn) {
    if (mutationTimer) return false; // one at a time; the engine re-runs after it
    var wait = Math.max(0, lastMutationAt + MUTATION_COOLDOWN - Date.now());
    mutationTimer = setTimeout(function () {
      mutationTimer = null;
      lastMutationAt = Date.now();
      fn();
    }, wait);
    return true;
  }

  /* ----------------------------------------------------------------------
     Session-scoped memories
     "dismissed": the shopper manually removed an unlocked reward — respect it
     (show the Re-Add card) until the cart drops below the threshold again.
     "failed": /cart/add.js rejected the reward — treat it like a dismissal
     instead of retrying forever.
     ---------------------------------------------------------------------- */

  function readList(key) {
    try {
      return JSON.parse(sessionStorage.getItem(key) || '[]') || [];
    } catch (e) {
      return [];
    }
  }

  function writeList(key, list) {
    try {
      sessionStorage.setItem(key, JSON.stringify(list));
    } catch (e) {
      /* storage blocked (private mode): degrade to always auto-adding */
    }
  }

  function inList(key, slot) {
    return readList(key).indexOf(slot) !== -1;
  }

  function addToList(key, slot) {
    var list = readList(key);
    if (list.indexOf(slot) === -1) {
      list.push(slot);
      writeList(key, list);
    }
  }

  function removeFromList(key, slot) {
    writeList(
      key,
      readList(key).filter(function (entry) {
        return entry !== slot;
      })
    );
  }

  /* ----------------------------------------------------------------------
     Reading the server-rendered state
     ---------------------------------------------------------------------- */

  function readRewardsState() {
    var container = document.querySelector('[data-js-faan-reward-cards]');
    var script = container && container.querySelector('[data-js-faan-rewards-data]');
    if (!container || !script) return null;
    try {
      var rewards = JSON.parse(script.textContent || '{}').rewards;
      return Array.isArray(rewards) ? { container: container, rewards: rewards } : null;
    } catch (error) {
      // A malformed snapshot would silently disable the whole engine.
      console.warn('[faan-cart] invalid rewards state JSON', error);
      return null;
    }
  }

  function freshReward(slot) {
    var state = readRewardsState();
    if (!state) return null;
    for (var i = 0; i < state.rewards.length; i++) {
      if (state.rewards[i].slot === slot) return state.rewards[i];
    }
    return null;
  }

  function cartSidebar() {
    return document.getElementById('site-cart-sidebar');
  }

  function isDrawerOpen() {
    var sidebar = cartSidebar();
    return !!sidebar && sidebar.classList.contains('sidebar--opened');
  }

  function setCheckoutActionsDisabled(state) {
    var nodes = document.querySelectorAll('#AjaxCartSubtotal #CheckOut, #AjaxCartSubtotal #ViewCart');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].classList.toggle('faan-cart-actions-disabled', state);
    }
  }

  /* ----------------------------------------------------------------------
     Cart mutations
     ---------------------------------------------------------------------- */

  // Re-render everything through the theme's canonical entry point. `false`
  // so a background auto-add never pops the drawer open by itself.
  function refreshCart() {
    selfRefreshing = true;
    if (typeof window.refreshCart === 'function') {
      window.refreshCart(false);
    }
    setTimeout(function () {
      selfRefreshing = false;
    }, 800);
  }

  /**
   * Adds one unit of a variant, mirroring ProductForm.onSubmitHandler
   * (assets/component-product-form.js): urlencoded POST to cart_add_url.js.
   */
  function addLine(variantId, properties) {
    var params = new URLSearchParams();
    params.set('id', String(variantId));
    params.set('quantity', '1');
    if (properties) {
      Object.keys(properties).forEach(function (key) {
        params.set('properties[' + key + ']', properties[key]);
      });
    }

    return fetch(routes().cart_add_url + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: params.toString()
    }).then(function (response) {
      return response.json().then(function (json) {
        return { ok: response.ok && !json.status, json: json };
      });
    });
  }

  /**
   * Removes a line. Uses the line item KEY, not the line index: the mutation
   * fires up to MUTATION_COOLDOWN after the pass that decided it, by which
   * time a concurrent action may have shifted the indexes.
   */
  function changeLineQuantity(lineKey, quantity) {
    return fetch(routes().cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ id: lineKey, quantity: quantity })
    }).then(function (response) {
      return response.json();
    });
  }

  function removeLine(lineKey) {
    return changeLineQuantity(lineKey, 0);
  }

  /* ----------------------------------------------------------------------
     Reward slot claims — the preventive layer

     A reward threshold grants exactly ONE gift. Every surface that can redeem
     one (the picker sheet and the in-drawer reward group) takes a claim on the
     slot BEFORE anything async starts. The claim is taken synchronously, so two
     taps in the same tick cannot both get through — including taps on two
     DIFFERENT products of the same threshold, which the quantity check further
     down cannot catch because each one creates its own line at quantity 1.
     ---------------------------------------------------------------------- */

  var slotClaims = {}; // slot -> true while an add for that slot is in flight
  var groupClaims = []; // claims taken by an in-drawer reward group

  function claimSlot(slot) {
    var key = String(slot);
    if (slotClaims[key]) return false;
    slotClaims[key] = true;
    return true;
  }

  function releaseSlot(slot) {
    delete slotClaims[String(slot)];
  }

  function setRewardGroupLocked(group, locked) {
    if (!group) return;
    group.classList.toggle('faan-cart-rewards__group--locked', locked);
    var buttons = group.querySelectorAll('button[name="add"], button[type="submit"]');
    for (var i = 0; i < buttons.length; i++) buttons[i].disabled = locked;
  }

  function releaseGroupClaim(index) {
    var claim = groupClaims[index];
    if (!claim) return;
    clearTimeout(claim.timer);
    releaseSlot(claim.slot);
    if (claim.node.isConnected) setRewardGroupLocked(claim.node, false);
    groupClaims.splice(index, 1);
  }

  /**
   * A successful add re-renders #AjaxCartForm, which REPLACES the group node.
   * A disconnected node is therefore proof the add landed and the claim can be
   * dropped — more precise than waiting for the safety timer.
   */
  function releaseSettledGroupClaims() {
    for (var i = groupClaims.length - 1; i >= 0; i--) {
      if (!groupClaims[i].node.isConnected) releaseGroupClaim(i);
    }
  }

  /**
   * Every "Add" in the in-drawer reward group is a native <product-form>, which
   * the theme submits on its own — the engine never sees it. Repeat taps on the
   * SAME button are already swallowed by `.button--loader.working`
   * (pointer-events: none), but nothing stopped a tap on a DIFFERENT product of
   * the same threshold from landing a second gift while the first add was still
   * in flight. This is the same protection the picker sheet applies, brought to
   * the block the shopper uses when they dismissed the modal and come back to
   * redeem later.
   *
   * Capture phase, so the second submit is cancelled before ProductForm sees it.
   */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.closest) return;

      var group = form.closest('[data-js-faan-reward-card]');
      if (!group) return;

      var slot = group.getAttribute('data-slot');
      if (!slot) return;

      if (!claimSlot(slot)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      setRewardGroupLocked(group, true);
      groupClaims.push({
        slot: slot,
        node: group,
        timer: setTimeout(function () {
          for (var i = groupClaims.length - 1; i >= 0; i--) {
            if (groupClaims[i].node === group) releaseGroupClaim(i);
          }
        }, SLOT_CLAIM_TIMEOUT)
      });
    },
    true
  );

  /**
   * Cart-integrity guard for freebies — the corrective layer.
   *
   * Two anomalies are repaired here:
   *
   *   1. MORE THAN ONE GIFT FOR THE SAME THRESHOLD. Two adds landing close
   *      enough together each create their own line, both at quantity 1, so the
   *      quantity check below is blind to them. The extra lines are removed and
   *      the oldest is kept.
   *   2. A FREEBIE LINE AT QUANTITY 2+. Re-adding a variant already in the cart
   *      with identical properties makes Shopify MERGE the lines and bump the
   *      quantity instead of creating a second one.
   *
   * The claims above prevent both from the UI; this catches whatever still gets
   * through. It is deliberately built on what the SERVER rendered rather than on
   * anything the client believes, so it also repairs adds this engine never saw
   * — a second tab, a restored cart, a hand-made /cart/add.
   *
   * It stands down while one of our own mutations is in flight: a reward "swap"
   * legitimately holds two lines for one slot between its add and its removal.
   *
   * @returns {boolean} true when a correction is pending, so the caller keeps
   *   the checkout CTAs disabled for this pass.
   */
  function enforceFreebieIntegrity() {
    if (engineBusy()) return false;

    var rows = document.querySelectorAll('[data-js-cart-item][data-faan-freebie="true"]');
    var slotSeen = {};

    for (var i = 0; i < rows.length; i++) {
      var lineKey = rows[i].getAttribute('data-id');
      if (!lineKey) continue;

      var slot = (rows[i].getAttribute('data-reward-slot') || '').trim();
      var quantity = parseInt(rows[i].getAttribute('data-qty'), 10) || 1;

      // (1) this threshold already has a gift -> drop the duplicate
      if (slot) {
        if (slotSeen[slot]) return correctFreebieLine(lineKey, 0, 'duplicate slot ' + slot);
        slotSeen[slot] = true;
      }

      // (2) merged line -> back to a single unit
      if (quantity > 1) return correctFreebieLine(lineKey, 1, 'merged quantity');
    }

    return false;
  }

  // One correction per pass: the resulting re-render starts the next one.
  function correctFreebieLine(lineKey, quantity, reason) {
    var flightKey = 'line:' + lineKey;
    if (inFlight[flightKey]) return true;

    inFlight[flightKey] = true;
    var scheduled = runMutationSpaced(function () {
      changeLineQuantity(lineKey, quantity)
        .then(function () {
          notify('krown:cart:update', { source: 'faan-cart-freebie-integrity' });
          refreshCart();
        })
        .catch(function (error) {
          console.warn('[faan-cart] freebie integrity correction failed (' + reason + ')', error);
        })
        .then(function () {
          inFlight[flightKey] = false;
          schedulePass();
        });
    });

    // Another mutation is already queued; retry on the next pass.
    if (!scheduled) inFlight[flightKey] = false;
    return true;
  }

  function notify(eventType, detail) {
    if (window.KROWN && window.KROWN.functions && window.KROWN.functions.eventDispatcher) {
      window.KROWN.functions.eventDispatcher(eventType, detail || {});
    }
  }

  // Auto-adds a single-product free_item reward (multi-product rewards go
  // through the picker sheet instead).
  function autoAddReward(slot) {
    if (inFlight[slot]) return;

    var reward = freshReward(slot);
    var stillWanted =
      reward &&
      reward.unlocked &&
      !reward.redeemed &&
      reward.products &&
      reward.products.length === 1 &&
      !inList(DISMISS_KEY, slot) &&
      !inList(FAILED_KEY, slot);

    if (!stillWanted) {
      schedulePass(); // re-enable the CTAs disabled for this pending operation
      return;
    }

    inFlight[slot] = true;
    var variantId = reward.products[0].variantId;

    addLine(variantId, { _omaggio: 'true', _reward: String(slot) })
      .then(function (result) {
        if (!result.ok) {
          // A persistent rejection (e.g. 422 out of stock) must not be retried
          // forever: remember it and fall back to the manual Re-Add card.
          addToList(FAILED_KEY, slot);
          console.warn('[faan-cart] reward auto-add rejected', result.json);
        } else {
          notify('krown:cart:add', { variantId: variantId, source: 'faan-cart-rewards' });
        }
        refreshCart();
      })
      .catch(function (error) {
        console.warn('[faan-cart] reward auto-add failed', error);
      })
      .then(function () {
        inFlight[slot] = false;
        schedulePass();
      });
  }

  // Removes the reward line once the cart drops below its threshold.
  function autoRemoveReward(lineKey, slot) {
    if (inFlight[slot]) return;

    var reward = freshReward(slot);
    if (!reward || reward.unlocked || reward.lineKey !== lineKey) {
      schedulePass();
      return;
    }

    inFlight[slot] = true;

    removeLine(lineKey)
      .then(function () {
        notify('krown:cart:update', { source: 'faan-cart-rewards' });
        refreshCart();
      })
      .catch(function (error) {
        console.warn('[faan-cart] reward auto-remove failed', error);
      })
      .then(function () {
        inFlight[slot] = false;
        schedulePass();
      });
  }

  // Empties the cart when nothing but reward lines remain.
  function clearRewardOnlyCart() {
    if (clearingCart) return;
    clearingCart = true;
    setCheckoutActionsDisabled(true);

    var scheduled = runMutationSpaced(function () {
      fetch(routes().cart_url + '/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: '{}'
      })
        .then(function () {
          refreshCart();
        })
        .catch(function (error) {
          console.warn('[faan-cart] cart clear failed', error);
        })
        .then(function () {
          clearingCart = false;
          schedulePass();
        });
    });

    if (!scheduled) {
      clearingCart = false; // another mutation is queued; retry next pass
    }
  }

  /* ----------------------------------------------------------------------
     Unlock celebration
     Rewards already unlocked on page load are baselined silently: only
     locked → unlocked transitions observed in-session get confetti.
     ---------------------------------------------------------------------- */

  function loadConfetti() {
    if (window.confetti) return Promise.resolve(window.confetti);
    if (!confettiPromise) {
      var url = window.KROWN && window.KROWN.settings && window.KROWN.settings.faan_confetti_url;
      if (!url || !window.KrownAssetLoader) return Promise.reject(new Error('confetti asset unavailable'));
      window.KrownAssetLoader.loadScript(url);
      confettiPromise = new Promise(function (resolve, reject) {
        var tries = 0;
        var timer = setInterval(function () {
          if (window.confetti) {
            clearInterval(timer);
            resolve(window.confetti);
          } else if (++tries > 100) {
            clearInterval(timer);
            reject(new Error('confetti load timeout'));
          }
        }, 50);
      });
    }
    return confettiPromise;
  }

  function fireUnlockConfetti(slot) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var sidebar = cartSidebar();
    if (!sidebar) return;

    loadConfetti()
      .then(function (confetti) {
        if (!confettiContext || !confettiContext.canvas.isConnected) {
          var canvas = document.createElement('canvas');
          canvas.className = 'faan-cart-confetti';
          canvas.setAttribute('aria-hidden', 'true');
          sidebar.appendChild(canvas);
          confettiContext = {
            canvas: canvas,
            instance: confetti.create(canvas, { resize: true, useWorker: false })
          };
        }

        // Burst from the unlocked milestone when it is on screen
        var origin = { x: 0.5, y: 0.3 };
        var milestone = document.querySelector('.faan-cart-rewards__milestone[data-milestone-slot="' + slot + '"]');
        if (milestone) {
          var sidebarRect = sidebar.getBoundingClientRect();
          var rect = milestone.getBoundingClientRect();
          if (sidebarRect.width > 0 && sidebarRect.height > 0) {
            origin = {
              x: (rect.left + rect.width / 2 - sidebarRect.left) / sidebarRect.width,
              y: (rect.top + rect.height / 2 - sidebarRect.top) / sidebarRect.height
            };
          }
        }

        var colors = ['#8cc63f', '#6f0987', '#ffffff', '#f4d35e'];
        confettiContext.instance({
          particleCount: 90,
          spread: 75,
          startVelocity: 32,
          scalar: 0.9,
          ticks: 220,
          origin: origin,
          colors: colors,
          disableForReducedMotion: true
        });
        setTimeout(function () {
          if (!confettiContext) return;
          confettiContext.instance({
            particleCount: 40,
            spread: 110,
            startVelocity: 22,
            scalar: 0.75,
            ticks: 180,
            origin: origin,
            colors: colors,
            disableForReducedMotion: true
          });
        }, 180);
      })
      .catch(function (error) {
        console.warn('[faan-cart] confetti unavailable', error);
      });
  }

  function maybeCelebrate(rewards) {
    if (!celebrationBaselineDone) {
      celebrationBaselineDone = true;
      rewards.forEach(function (reward) {
        if (reward.unlocked) addToList(CELEBRATED_KEY, reward.slot);
      });
      return;
    }

    rewards.forEach(function (reward) {
      if (!reward.unlocked) removeFromList(CELEBRATED_KEY, reward.slot); // re-arm
    });

    if (!isDrawerOpen()) return;

    for (var i = 0; i < rewards.length; i++) {
      if (rewards[i].unlocked && !inList(CELEBRATED_KEY, rewards[i].slot)) {
        addToList(CELEBRATED_KEY, rewards[i].slot);
        fireUnlockConfetti(rewards[i].slot);
        break; // one burst per pass
      }
    }
  }

  /* ----------------------------------------------------------------------
     The rewards engine pass
     ---------------------------------------------------------------------- */

  function getRewardsSheet() {
    return document.querySelector('faan-cart-rewards-sheet');
  }

  /**
   * Applies reward-box visibility from the current snapshot. Pure DOM reads and
   * `hidden` writes: no fetches, no mutations, safe to call as often as needed.
   *
   * It runs SYNCHRONOUSLY from the MutationObserver, before the debounced pass,
   * so a re-rendered box never blinks off and back on: the server already ships
   * the multi-product picker in the right state, and this restores the
   * session-scoped "Re-Add" rows in the same frame as the swap.
   */
  function applyRewardVisibility() {
    var state = readRewardsState();
    if (!state) return;

    var anyVisible = false;

    state.rewards.forEach(function (reward) {
      if (reward.type !== 'free_item') return;
      var group = state.container.querySelector(
        '[data-js-faan-reward-card][data-slot="' + reward.slot + '"]'
      );
      if (!group) return;

      var isMulti = reward.products && reward.products.length > 1;
      var show = false;

      if (reward.unlocked && !reward.redeemed) {
        show = isMulti || inList(DISMISS_KEY, reward.slot) || inList(FAILED_KEY, reward.slot);
      }

      group.hidden = !show;
      if (show) anyVisible = true;
    });

    state.container.hidden = !anyVisible;
  }

  function updateRewards() {
    var state = readRewardsState();
    if (!state || clearingCart) {
      var sheet = getRewardsSheet();
      if (sheet && sheet.close) sheet.close();
      return;
    }

    var container = state.container;
    var rewards = state.rewards;

    // Only reward lines left: block the CTAs and empty the cart
    if (container.getAttribute('data-only-rewards') === 'true') {
      setCheckoutActionsDisabled(true);
      var openSheet = getRewardsSheet();
      if (openSheet && openSheet.close) openSheet.close();
      clearRewardOnlyCart();
      return;
    }

    var pendingRemove = [];
    var pendingAdd = [];
    var pendingSheet = [];

    rewards.forEach(function (reward) {
      if (reward.type !== 'free_item') return;

      var slot = reward.slot;
      var isMulti = reward.products && reward.products.length > 1;

      if (!reward.unlocked) {
        // Re-arm everything once the shopper re-crosses the threshold
        removeFromList(DISMISS_KEY, slot);
        removeFromList(FAILED_KEY, slot);
        delete snoozedSlots[slot];
        if (reward.lineKey) pendingRemove.push({ key: reward.lineKey, slot: slot });
        lastRedeemed[slot] = false;
        return;
      }

      if (reward.redeemed) {
        lastRedeemed[slot] = true;
        return;
      }

      // Unlocked and not redeemed
      if (isMulti) {
        // The inline picker box is the persistent fallback (rendered visible by
        // the server); the modal is only the proactive prompt, so it respects
        // the snooze set when the shopper dismissed it.
        if (!snoozedSlots[slot]) pendingSheet.push(reward);
      } else if (!inList(DISMISS_KEY, slot) && !inList(FAILED_KEY, slot)) {
        if (lastRedeemed[slot] === true) {
          addToList(DISMISS_KEY, slot); // shopper removed an auto-added reward
        } else {
          pendingAdd.push(reward);
        }
      }
      lastRedeemed[slot] = false;
    });

    // Visibility is owned by applyRewardVisibility() so that the observer can
    // restore it synchronously; run it here too, after the bookkeeping above may
    // have flipped a reward into the "dismissed" state.
    applyRewardVisibility();

    maybeCelebrate(rewards);

    // Free-gift picker: one pending reward at a time, lowest threshold first
    // (the snapshot is already threshold-sorted). Drawer must be open.
    var rewardsSheet = getRewardsSheet();
    if (rewardsSheet) {
      if (pendingSheet.length > 0 && isDrawerOpen()) {
        rewardsSheet.openFor(pendingSheet[0]);
      } else if (rewardsSheet.close && !rewardsSheet.manual) {
        // `manual` = opened by the shopper through "Change"; the engine must not
        // close it just because no reward is pending.
        rewardsSheet.close();
      }
    }

    // One mutation per pass, removals first; the resulting re-render triggers
    // the next pass through the observer.
    if (pendingRemove.length > 0) {
      runMutationSpaced(function () {
        autoRemoveReward(pendingRemove[0].key, pendingRemove[0].slot);
      });
    } else if (pendingAdd.length > 0) {
      runMutationSpaced(function () {
        autoAddReward(pendingAdd[0].slot);
      });
    }

    setCheckoutActionsDisabled(pendingRemove.length > 0 || pendingAdd.length > 0 || engineBusy());
  }

  function updateUpsell() {
    var nodes = document.querySelectorAll('faan-cart-upsell');
    for (var i = 0; i < nodes.length; i++) {
      if (typeof nodes[i].update === 'function') nodes[i].update();
    }
  }

  // The footer's "continue shopping" button just closes the drawer. It lives
  // inside #AjaxCartSubtotal, which is re-rendered on every cart mutation, so
  // it is handled by delegation rather than a per-node listener.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-js-faan-continue]');
    if (!trigger) return;
    event.preventDefault();
    var sidebar = cartSidebar();
    if (sidebar && typeof sidebar.hide === 'function') sidebar.hide();
  });

  // "Change" on a redeemed reward line reopens the picker for that reward.
  // Delegated because the line items are re-rendered on every cart mutation.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-js-faan-reward-change]');
    if (!trigger) return;
    event.preventDefault();

    var slot = Number(trigger.getAttribute('data-slot'));
    var reward = freshReward(slot);
    if (!reward || !reward.products || reward.products.length < 2) return;

    delete snoozedSlots[slot];
    var sheet = getRewardsSheet();
    if (sheet && typeof sheet.openFor === 'function') {
      // Force a re-render even if the sheet is already bound to this slot
      sheet.currentSlot = null;
      sheet.openFor(reward, true);
    }
  });

  // The gift block is persistent (it sits below the upsell, outside the
  // re-rendered cart form), so its cart-dependent visibility is applied from
  // the [data-js-faan-gift-visible] mirror published by cart-form.liquid.
  function updateGift() {
    var gift = document.querySelector('[data-js-faan-gift]');
    if (!gift) return;
    var mirror = document.querySelector('[data-js-faan-gift-visible]');
    var visible = !!mirror && mirror.textContent.trim() === 'true';
    gift.hidden = !visible;
  }

  function runPass() {
    // A re-render replaced the node that held the claim: the add has landed.
    releaseSettledGroupClaims();
    // Runs first: a duplicated or merged freebie is a cart-integrity problem,
    // and correcting it re-renders everything the other updaters read anyway.
    var correcting = enforceFreebieIntegrity();
    updateRewards();
    updateUpsell();
    updateGift();
    if (correcting) setCheckoutActionsDisabled(true);
  }

  function schedulePass() {
    if (passTimer) clearTimeout(passTimer);
    passTimer = setTimeout(function () {
      passTimer = null;
      runPass();
    }, PASS_DEBOUNCE);
  }

  /* ----------------------------------------------------------------------
     Custom element: rotating messages
     ---------------------------------------------------------------------- */

  function FaanCartMessagesFactory() {
    class FaanCartMessages extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-js-track]');
        this.count = Number(this.dataset.count) || 0;
        this.index = 0;
        if (!this.track || this.count < 2) return;
        var speed = (Number(this.dataset.speed) || 4) * 1000;
        this.timer = setInterval(this.advance.bind(this), speed);
      }

      disconnectedCallback() {
        if (this.timer) clearInterval(this.timer);
        this.timer = null;
      }

      advance() {
        if (this.index >= this.count) {
          // Entered the duplicate half: snap back without transition
          this.track.classList.add('no-transition');
          this.index -= this.count;
          this.apply();
          void this.track.offsetWidth; // force reflow so the snap is instant
          this.track.classList.remove('no-transition');
        }
        this.index += 1;
        this.apply();
      }

      apply() {
        this.track.style.transform = 'translateX(-' + this.index * 100 + '%)';
      }
    }
    return FaanCartMessages;
  }

  /* ----------------------------------------------------------------------
     Custom element: upsell list (vertical rows)
     ---------------------------------------------------------------------- */

  function FaanCartUpsellFactory() {
    class FaanCartUpsell extends HTMLElement {
      constructor() {
        super();
        this.init();
      }

      init() {
        this.track = this.querySelector('[data-js-track]');
        if (!this.track) return;

        var self = this;
        this.addEventListener('click', function (event) {
          var trigger = event.target.closest('[data-js-faan-choose-variant]');
          if (trigger) self.openVariantSheet(trigger);
        });

        this.update();
      }

      openVariantSheet(trigger) {
        var card = trigger.closest('.faan-cart-upsell-item');
        var dataEl = card && card.querySelector('[data-js-faan-variant-data]');
        if (!dataEl) return;

        var data;
        try {
          data = JSON.parse(dataEl.textContent || '');
        } catch (e) {
          return;
        }

        var sheet = document.querySelector('faan-cart-variant-sheet');
        if (sheet && typeof sheet.open === 'function') sheet.open(data, trigger);
      }

      update() {
        if (!this.track) return;

        // Static mode: cards are server-rendered from the chosen collection
        if (this.dataset.static === 'true') {
          this.applyCartFilter();
          return;
        }

        // Dynamic mode: the base product comes from a hidden mirror rendered
        // inside #AjaxCartForm, so it follows the cart contents.
        var mirror = document.querySelector('[data-js-faan-first-product-id]');
        var productId = mirror && mirror.textContent ? mirror.textContent.trim() : '';

        if (!productId) {
          this.classList.remove('is-visible');
          upsellCache.productId = null;
          return;
        }

        if (upsellCache.productId === productId) {
          // Already loaded for this base product; re-inject if needed
          if (this.track.children.length === 0 && upsellCache.html) {
            this.track.innerHTML = upsellCache.html;
          }
          this.applyCartFilter();
          return;
        }
        upsellCache.productId = productId;

        var self = this;
        var limit = this.dataset.limit || 6;
        var url =
          this.dataset.recommendationsUrl +
          '?section_id=' +
          this.dataset.section +
          '&product_id=' +
          productId +
          '&limit=' +
          limit +
          '&intent=related';

        fetch(url)
          .then(function (response) {
            return response.text();
          })
          .then(function (text) {
            var marker = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('[data-js-faan-upsell-performed]');
            var inner = marker ? marker.innerHTML.trim() : '';
            upsellCache.html = inner;
            if (!inner) {
              self.classList.remove('is-visible');
              return;
            }
            self.track.innerHTML = inner;
            self.applyCartFilter();
          })
          .catch(function (error) {
            upsellCache.productId = null; // allow a retry on the next change
            console.warn('[faan-cart] upsell load failed', error);
          });
      }

      /**
       * Hides the rows whose product is already in the cart (and shows them
       * again once it is removed), then reflects whether anything is left to
       * show on the component itself.
       */
      applyCartFilter() {
        if (!this.track) return;

        var inCart = {};
        var cartRows = document.querySelectorAll('[data-js-cart-item][data-product-id]');
        for (var i = 0; i < cartRows.length; i++) {
          inCart[cartRows[i].getAttribute('data-product-id')] = true;
        }

        var rows = this.track.querySelectorAll('[data-js-faan-upsell-item]');
        var visible = 0;
        for (var j = 0; j < rows.length; j++) {
          var id = rows[j].getAttribute('data-product-id');
          var hide = !!(id && inCart[id]);
          rows[j].hidden = hide;
          if (!hide) visible++;
        }

        this.classList.toggle('is-visible', visible > 0);
      }
    }
    return FaanCartUpsell;
  }

  /* ----------------------------------------------------------------------
     Shared bottom-sheet behaviour
     ---------------------------------------------------------------------- */

  function openSheet(el) {
    if (!el.hidden) return;
    el.hidden = false;
    // Double rAF so the slide-up transition plays from the hidden state
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        el.classList.add('is-open');
      });
    });

    // Capture ESC before SidebarDrawer's document listener closes the drawer
    el.__escHandler = function (event) {
      if (event.key !== 'Escape' && event.keyCode !== 27) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (typeof el.closeByUser === 'function') el.closeByUser();
      else el.close();
    };
    document.addEventListener('keydown', el.__escHandler, true);
  }

  function closeSheet(el) {
    if (el.hidden) return;
    if (el.__escHandler) {
      document.removeEventListener('keydown', el.__escHandler, true);
      el.__escHandler = null;
    }
    el.classList.remove('is-open');

    var finished = false;
    var finish = function () {
      if (finished) return;
      finished = true;
      el.hidden = true;
    };
    var panel = el.querySelector('[data-js-panel]');
    if (panel) panel.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, 400);
  }

  // NB: the close hook is `data-js-faan-sheet-close`, NOT the theme's
  // `data-js-close`. MainHeader binds a click handler to every
  // `sidebar-drawer [data-js-close]` that hides the WHOLE drawer
  // (section-header.js:152), so reusing that attribute would close the cart
  // instead of just the sheet.
  function wireSheetDismissers(el, handler) {
    var closers = el.querySelectorAll('[data-js-faan-sheet-close]');
    for (var i = 0; i < closers.length; i++) {
      closers[i].addEventListener('click', handler);
    }
  }

  /* ----------------------------------------------------------------------
     Custom element: free-gift picker sheet
     ---------------------------------------------------------------------- */

  function FaanCartRewardsSheetFactory() {
    class FaanCartRewardsSheet extends HTMLElement {
      constructor() {
        super();
        this.init();
      }

      init() {
        this.list = this.querySelector('[data-js-list]');
        this.errorMessage = this.querySelector('[data-js-error]');
        this.currentSlot = null;
        this.redeeming = false;
        var self = this;
        wireSheetDismissers(this, function () {
          self.closeByUser();
        });
      }

      /**
       * @param {object} reward - reward entry from the server-rendered snapshot
       * @param {boolean} [manual] - true when opened by the shopper via "Change";
       *   the engine then leaves the sheet alone until it is closed explicitly,
       *   and redeeming swaps the currently redeemed line for the new pick.
       */
      openFor(reward, manual) {
        if (!reward || !reward.products || reward.products.length === 0) return;
        // Already showing this reward: keep the DOM (avoids flicker every pass)
        if (!this.hidden && this.currentSlot === reward.slot) return;

        this.currentSlot = reward.slot;
        this.manual = manual === true;
        this.replacingLineKey = this.manual ? reward.lineKey || null : null;
        this.redeemedVariantId = reward.redeemedVariantId || null;
        this.redeeming = false;
        if (this.errorMessage) this.errorMessage.hidden = true;
        this.renderRows(reward);
        openSheet(this);
      }

      // X / overlay / ESC: snooze every pending multi-product reward until the
      // drawer is reopened.
      closeByUser() {
        var state = readRewardsState();
        if (state) {
          state.rewards.forEach(function (reward) {
            if (
              reward.type === 'free_item' &&
              reward.unlocked &&
              !reward.redeemed &&
              reward.products &&
              reward.products.length > 1
            ) {
              snoozedSlots[reward.slot] = true;
            }
          });
        }
        this.close();
      }

      close() {
        this.currentSlot = null;
        this.manual = false;
        this.replacingLineKey = null;
        this.redeemedVariantId = null;
        closeSheet(this);
      }

      renderRows(reward) {
        if (!this.list) return;
        this.list.textContent = '';

        var self = this;
        reward.products.forEach(function (product) {
          var row = document.createElement('div');
          row.className = 'faan-cart-sheet__row';

          if (product.image) {
            var img = document.createElement('img');
            img.className = 'faan-cart-sheet__row-image';
            img.src = product.image;
            img.alt = product.title || '';
            img.width = 64;
            img.height = 64;
            img.loading = 'lazy';
            row.appendChild(img);
          }

          var info = document.createElement('div');
          info.className = 'faan-cart-sheet__row-info';

          var title = document.createElement('p');
          title.className = 'faan-cart-sheet__row-title text-size--small';
          title.textContent = product.title || '';
          info.appendChild(title);

          var badge = document.createElement('span');
          badge.className = 'faan-cart-sheet__row-badge text-size--xsmall';
          badge.textContent = self.dataset.badgeLabel || '';
          info.appendChild(badge);

          row.appendChild(info);

          var isCurrent =
            self.redeemedVariantId != null && String(product.variantId) === String(self.redeemedVariantId);

          var button = document.createElement('button');
          button.type = 'button';
          button.className = 'faan-cart-sheet__row-button text-size--small';

          if (isCurrent) {
            row.classList.add('faan-cart-sheet__row--current');
            button.disabled = true;
            button.textContent = self.dataset.currentLabel || '';
          } else {
            button.textContent = self.dataset.addLabel || '';
            button.addEventListener('click', function () {
              self.redeem(reward.slot, product.variantId, button);
            });
          }

          row.appendChild(button);

          self.list.appendChild(row);
        });
      }

      /**
       * Enables or disables every option row at once. The row for the gift
       * already in the cart stays disabled either way — it is informational,
       * not selectable.
       */
      setRowsDisabled(disabled) {
        if (!this.list) return;
        var rows = this.list.querySelectorAll('.faan-cart-sheet__row');
        for (var i = 0; i < rows.length; i++) {
          var rowButton = rows[i].querySelector('button');
          if (!rowButton) continue;
          rowButton.disabled =
            disabled || rows[i].classList.contains('faan-cart-sheet__row--current');
        }
      }

      redeem(slot, variantId, button) {
        if (this.redeeming) return;

        // Picking the gift that is already redeemed is a no-op, never a swap:
        // the add would merge into the existing line and the follow-up removal
        // would delete it. Guarded here too, not just in the UI, because the
        // snapshot can be a pass behind the DOM.
        if (this.redeemedVariantId != null && String(variantId) === String(this.redeemedVariantId)) {
          this.close();
          return;
        }

        // One gift per threshold. Taken synchronously, before any await, so a
        // second tap cannot slip in — here or in the in-drawer reward group,
        // which competes for the same slot.
        if (!claimSlot(slot)) return;

        this.redeeming = true;
        button.disabled = true;
        // Lock EVERY option, not just the one tapped: the sheet stays on screen
        // until the cart comes back, and a tap on another product of the same
        // threshold would otherwise add a second gift.
        this.setRowsDisabled(true);

        redeemingReward = true;
        setCheckoutActionsDisabled(true);
        if (this.errorMessage) this.errorMessage.hidden = true;

        var self = this;
        // A gift already redeemed for this threshold must be REPLACED, never
        // joined. "Change" passes that line explicitly; for every other entry
        // point the current snapshot is consulted, so an add that would have
        // become a second gift for the slot is turned into a swap instead.
        var replacing = this.replacingLineKey;
        if (!replacing) {
          var current = freshReward(slot);
          if (current && current.redeemed && current.lineKey) replacing = current.lineKey;
        }

        addLine(variantId, { _omaggio: 'true', _reward: String(slot) })
          .then(function (result) {
            if (!result.ok) {
              if (self.errorMessage) self.errorMessage.hidden = false;
              self.setRowsDisabled(false);
              return;
            }

            notify('krown:cart:add', { variantId: variantId, source: 'faan-cart-rewards' });
            // Close straight away instead of waiting for the next pass: the gap
            // between the add and the re-render is exactly when a second option
            // could still be tapped. close() also clears currentSlot, manual,
            // replacingLineKey and redeemedVariantId.
            self.close();

            if (replacing) {
              return removeLine(replacing)
                .catch(function (error) {
                  console.warn('[faan-cart] swapping the reward line failed', error);
                })
                .then(function () {
                  refreshCart();
                });
            }
            refreshCart();
          })
          .catch(function (error) {
            console.warn('[faan-cart] reward redeem failed', error);
            if (self.errorMessage) self.errorMessage.hidden = false;
            self.setRowsDisabled(false);
          })
          .then(function () {
            self.redeeming = false;
            redeemingReward = false;
            releaseSlot(slot);
            schedulePass();
          });
      }
    }
    return FaanCartRewardsSheet;
  }

  /* ----------------------------------------------------------------------
     Custom element: variant picker sheet
     ---------------------------------------------------------------------- */

  function FaanCartVariantSheetFactory() {
    class FaanCartVariantSheet extends HTMLElement {
      constructor() {
        super();
        this.init();
      }

      init() {
        this.panel = this.querySelector('[data-js-panel]');
        this.media = this.querySelector('[data-js-media]');
        this.image = this.querySelector('[data-js-image]');
        this.titleEl = this.querySelector('[data-js-title]');
        this.priceEl = this.querySelector('[data-js-price]');
        this.optionLabel = this.querySelector('[data-js-option-label]');
        this.pills = this.querySelector('[data-js-pills]');
        this.errorMessage = this.querySelector('[data-js-error]');
        this.confirmButton = this.querySelector('[data-js-confirm]');

        this.variants = [];
        this.selectedId = null;
        this.trigger = null;
        this.adding = false;

        var self = this;
        wireSheetDismissers(this, function () {
          self.close();
        });
        if (this.confirmButton) {
          this.confirmButton.addEventListener('click', function () {
            self.confirm();
          });
        }
      }

      open(data, trigger) {
        if (!data || !Array.isArray(data.variants) || data.variants.length === 0) return;

        this.variants = data.variants;
        this.trigger = trigger || null;
        this.adding = false;

        if (this.titleEl) this.titleEl.textContent = data.title || '';
        if (this.panel) this.panel.setAttribute('aria-label', data.title || '');
        this.setImage(data);

        if (this.optionLabel) {
          this.optionLabel.textContent = data.optionLabel || '';
          this.optionLabel.hidden = !data.optionLabel;
        }
        if (this.errorMessage) this.errorMessage.hidden = true;
        if (this.confirmButton) this.confirmButton.disabled = false;

        var firstAvailable = this.variants.filter(function (variant) {
          return variant.available;
        })[0];
        this.selectedId = firstAvailable ? firstAvailable.id : null;

        this.renderPills();
        this.updatePrice();
        openSheet(this);
      }

      // Drop the previous product's frame immediately, then shimmer the
      // placeholder until the new image has actually loaded.
      setImage(data) {
        if (!this.image || !this.media) return;

        this.image.classList.remove('is-loaded');
        this.image.removeAttribute('src');
        this.image.onload = null;
        this.image.onerror = null;

        if (!data.image) {
          this.media.hidden = true;
          this.media.classList.remove('is-loading');
          return;
        }

        var self = this;
        this.media.hidden = false;
        this.media.classList.add('is-loading');
        this.image.alt = data.title || '';

        var reveal = function () {
          self.image.classList.add('is-loaded');
          self.media.classList.remove('is-loading');
        };
        this.image.onload = reveal;
        this.image.onerror = function () {
          self.media.classList.remove('is-loading');
        };
        this.image.src = data.image;
        if (this.image.complete && this.image.naturalWidth > 0) reveal();
      }

      close() {
        closeSheet(this);
        if (this.trigger && this.trigger.focus) this.trigger.focus();
        this.trigger = null;
      }

      renderPills() {
        if (!this.pills) return;
        this.pills.textContent = '';

        var self = this;
        this.variants.forEach(function (variant) {
          var pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'faan-cart-sheet__pill';
          pill.textContent = variant.title;
          pill.disabled = !variant.available;
          pill.setAttribute('aria-pressed', String(variant.id === self.selectedId));
          pill.addEventListener('click', function () {
            self.selectedId = variant.id;
            var siblings = self.pills.children;
            for (var i = 0; i < siblings.length; i++) {
              siblings[i].setAttribute('aria-pressed', String(siblings[i] === pill));
            }
            self.updatePrice();
            if (self.errorMessage) self.errorMessage.hidden = true;
          });
          self.pills.appendChild(pill);
        });
      }

      updatePrice() {
        if (!this.priceEl) return;
        var selected = this.variants.filter(
          function (variant) {
            return variant.id === this.selectedId;
          }.bind(this)
        )[0];
        this.priceEl.textContent = selected ? selected.price : '';
      }

      confirm() {
        if (this.adding || !this.selectedId) return;
        this.adding = true;
        if (this.confirmButton) this.confirmButton.disabled = true;
        if (this.errorMessage) this.errorMessage.hidden = true;

        var self = this;
        var variantId = this.selectedId;

        addLine(variantId, null)
          .then(function (result) {
            if (result.ok) {
              notify('krown:cart:add', { variantId: variantId, source: 'faan-cart-upsell' });
              self.close();
              refreshCart();
              return;
            }
            if (self.errorMessage) self.errorMessage.hidden = false;
          })
          .catch(function (error) {
            console.warn('[faan-cart] variant add failed', error);
            if (self.errorMessage) self.errorMessage.hidden = false;
          })
          .then(function () {
            self.adding = false;
            if (self.confirmButton) self.confirmButton.disabled = false;
            schedulePass();
          });
      }
    }
    return FaanCartVariantSheet;
  }

  /* ----------------------------------------------------------------------
     Registration (theme convention: guard both the class and the tag)
     ---------------------------------------------------------------------- */

  function define(tag, factory) {
    if (typeof customElements.get(tag) === 'undefined') {
      customElements.define(tag, factory());
    }
  }

  define('faan-cart-messages', FaanCartMessagesFactory);
  define('faan-cart-upsell', FaanCartUpsellFactory);
  define('faan-cart-rewards-sheet', FaanCartRewardsSheetFactory);
  define('faan-cart-variant-sheet', FaanCartVariantSheetFactory);

  /* ----------------------------------------------------------------------
     Reactivity
     There is no reliable global "cart changed" event in this theme:
     - `cart-updated` fires only from CartForm.updateCartQty
     - window.refreshCart is not called by updateCartQty nor by ProductForm
     - `krown:cart:update` is guarded by `if (updatedItem)` so it never fires
       on a removal — exactly the case the "dismissed" logic depends on.
     All three refresh paths DO replace the children of #AjaxCartForm, and that
     node itself is never swapped, so one observer installed once is enough.
     ---------------------------------------------------------------------- */

  function boot() {
    var form = document.getElementById('AjaxCartForm');
    if (form) {
      new MutationObserver(function () {
        // Restore the reward box state in the same frame as the DOM swap, so it
        // never blinks off while the debounced pass is pending.
        applyRewardVisibility();
        schedulePass();
      }).observe(form, { childList: true, subtree: false });
    }

    // CartDiscounts.handleSuccess re-renders ONLY the subtotal, but a
    // cart-level discount changes final_line_price and therefore the
    // qualifying total. Force a full refresh so the reward bar isn't stale.
    var subtotal = document.getElementById('AjaxCartSubtotal');
    if (subtotal) {
      new MutationObserver(function () {
        if (!selfRefreshing && !engineBusy()) refreshCart();
      }).observe(subtotal, { childList: true, subtree: false });
    }

    // Drawer opening: reset the snooze so the picker re-proposes itself.
    var sidebar = cartSidebar();
    if (sidebar) {
      drawerWasOpen = sidebar.classList.contains('sidebar--opened');
      new MutationObserver(function () {
        var open = sidebar.classList.contains('sidebar--opened');
        if (open && !drawerWasOpen) {
          snoozedSlots = {};
          schedulePass();
        }
        drawerWasOpen = open;
      }).observe(sidebar, { attributes: true, attributeFilter: ['class'] });
    }

    schedulePass();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

/* CUSTOM CODE END */
